/**
 * Billing dashboard -- subscription state, current-period usage, and plan
 * catalogue. Surfaces the live /dm-api/billing/* endpoints to org admins.
 */
import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../../services/billingApi';
import '../admin/admin.css';

function dollars(cents, currency = 'USD') {
  if (cents === null || cents === undefined) return 'Custom';
  const v = Number(cents) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency,
  }).format(v);
}

function StatusPill({ status }) {
  const tone = {
    active: 'green', trialing: 'blue', past_due: 'orange',
    paused: 'gray', cancelled: 'red',
  }[status] || 'gray';
  return (
    <span className={`pill pill--${tone}`} style={{
      padding: '2px 10px', borderRadius: 999, fontSize: 12,
      background: tone === 'green' ? '#e6f4ea'
                : tone === 'blue'  ? '#e8f0fe'
                : tone === 'orange' ? '#fef0e1'
                : tone === 'red'   ? '#fce8e6'
                : '#f0f0f0',
      color:      tone === 'green' ? '#137333'
                : tone === 'blue'  ? '#174ea6'
                : tone === 'orange' ? '#b06000'
                : tone === 'red'   ? '#a50e0e'
                : '#5f6368',
    }}>{status}</span>
  );
}

function UsageBar({ used, included }) {
  const usedNum = Number(used || 0);
  const incNum  = Number(included || 0);
  const ratio   = incNum > 0 ? Math.min(1, usedNum / incNum) : 0;
  const over    = usedNum > incNum;
  return (
    <div style={{ width: '100%', maxWidth: 480 }}>
      <div style={{
        height: 12, background: '#eef0f3', borderRadius: 6, overflow: 'hidden',
      }}>
        <div style={{
          width: `${ratio * 100}%`, height: '100%',
          background: over ? '#ea4335' : '#1a73e8',
        }} />
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 12, color: '#5f6368', marginTop: 4,
      }}>
        <span>{usedNum} used</span>
        <span>{incNum > 0 ? `${incNum} included` : 'Custom volume'}</span>
      </div>
    </div>
  );
}

export default function BillingDashboard() {
  const [plans, setPlans]               = useState(null);
  const [sub, setSub]                   = useState(null);
  const [usage, setUsage]               = useState(null);
  const [invoices, setInvoices]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [notice, setNotice]             = useState('');
  const [busy, setBusy]                 = useState(false);
  const [pickedPlan, setPickedPlan]     = useState('starter');
  const [pickedSeats, setPickedSeats]   = useState(1);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [planRes, subRes, useRes, invRes] = await Promise.all([
        api.fetchPublicPlans(),
        api.fetchSubscription(),
        api.fetchUsage(),
        api.fetchInvoices({ limit: 25 }),
      ]);
      setPlans(planRes?.data?.items || []);
      setSub(subRes?.data || null);
      setUsage(useRes?.data || null);
      setInvoices(invRes?.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleStart = async () => {
    setBusy(true);
    setError(''); setNotice('');
    try {
      const out = await api.startSubscription({
        planCode: pickedPlan,
        seats: Number(pickedSeats) || 1,
      });
      if (out?.data?.approvalUrl) {
        setNotice('Redirecting to PayPal to complete subscription...');
        window.location.href = out.data.approvalUrl;
        return;
      }
      setNotice('Subscription created. Sales will contact you for enterprise pricing.');
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not start subscription');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel subscription at the end of the current period?')) return;
    setBusy(true);
    setError(''); setNotice('');
    try {
      await api.cancelSubscription({ atPeriodEnd: true });
      setNotice('Cancellation scheduled for end of current period.');
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to cancel');
    } finally {
      setBusy(false);
    }
  };

  const handleSeats = async (delta) => {
    if (!sub?.seats) return;
    const next = Math.max(1, Number(sub.seats) + delta);
    setBusy(true);
    setError(''); setNotice('');
    try {
      await api.updateSeats(next);
      setNotice(`Seats updated to ${next}.`);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to update seats');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <h1>Billing</h1>
          <p>Subscription, usage, and invoices for your organization.</p>
        </div>
        <div>
          <button className="btn" onClick={refresh} disabled={loading || busy}>
            Refresh
          </button>
        </div>
      </div>

      {error  && <div className="admin-alert admin-alert--error">{error}</div>}
      {notice && <div className="admin-alert admin-alert--success">{notice}</div>}

      {/* Current subscription */}
      <div className="admin-card">
        <h3>Current subscription</h3>
        {loading ? (
          <p>Loading...</p>
        ) : sub ? (
          <dl className="admin-dl">
            <div><dt>Plan</dt><dd>
              {sub.planName} ({sub.planCode})
            </dd></div>
            <div><dt>Status</dt><dd><StatusPill status={sub.status} /></dd></div>
            <div><dt>Seats</dt><dd>
              {sub.seats}
              <button className="btn btn--small" style={{ marginLeft: 8 }}
                       onClick={() => handleSeats(-1)} disabled={busy || sub.seats <= 1}>−</button>
              <button className="btn btn--small" style={{ marginLeft: 4 }}
                       onClick={() => handleSeats(+1)} disabled={busy}>+</button>
            </dd></div>
            <div><dt>Monthly base</dt><dd>
              {sub.monthlyBaseCents != null
                ? `${dollars(sub.monthlyBaseCents)} × ${sub.seats} = ${dollars((sub.monthlyBaseCents || 0) * sub.seats)}`
                : 'Custom'}
            </dd></div>
            <div><dt>Included cases / user</dt><dd>{sub.includedCasesPerUser ?? 'Custom'}</dd></div>
            <div><dt>Overage rate</dt><dd>
              {sub.overagePerCaseCents != null
                ? `${dollars(sub.overagePerCaseCents)} / case`
                : 'Custom'}
            </dd></div>
            <div><dt>Period</dt><dd>
              {sub.currentPeriodStart?.slice(0, 10)} → {sub.currentPeriodEnd?.slice(0, 10)}
            </dd></div>
            {sub.cancelAtPeriodEnd && (
              <div><dt>Cancellation</dt><dd>
                Scheduled at end of current period.
              </dd></div>
            )}
            {sub.approvalUrl && sub.status === 'trialing' && !sub.paypalSubscriptionId?.startsWith('I-STUB') && (
              <div><dt>Action</dt><dd>
                <a className="btn btn--primary" href={sub.approvalUrl}
                    target="_blank" rel="noreferrer">
                  Complete PayPal approval
                </a>
              </dd></div>
            )}
          </dl>
        ) : (
          <p>No active subscription yet.</p>
        )}

        {sub && !sub.cancelAtPeriodEnd && sub.status !== 'cancelled' && (
          <div style={{ marginTop: 16 }}>
            <button className="btn btn--danger" onClick={handleCancel} disabled={busy}>
              Cancel at period end
            </button>
          </div>
        )}
      </div>

      {/* Current usage */}
      {sub && usage && (
        <div className="admin-card">
          <h3>Current period usage</h3>
          <UsageBar used={usage.usedCases} included={usage.includedCases} />
          <dl className="admin-dl" style={{ marginTop: 12 }}>
            <div><dt>Cases used</dt><dd>{usage.usedCases}</dd></div>
            <div><dt>Cases included</dt><dd>{usage.includedCases}</dd></div>
            <div><dt>Overage cases</dt><dd>{usage.overageCases}</dd></div>
            <div><dt>Base charge</dt><dd>{dollars(usage.baseCents)}</dd></div>
            <div><dt>Overage charge</dt><dd>{dollars(usage.overageCents)}</dd></div>
            <div><dt>Period total</dt>
              <dd><strong>{dollars(usage.totalCents)}</strong></dd></div>
          </dl>
        </div>
      )}

      {/* Plan picker (when there's no sub or sub is cancelled) */}
      {!sub && plans && (
        <div className="admin-card">
          <h3>Choose a plan</h3>
          <div style={{ display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                          gap: 16 }}>
            {plans.map((p) => (
              <label key={p.code} style={{
                border: pickedPlan === p.code
                  ? '2px solid #1a73e8' : '1px solid #dadce0',
                borderRadius: 8, padding: 16, cursor: 'pointer', display: 'block',
              }}>
                <input type="radio" name="plan" value={p.code}
                        checked={pickedPlan === p.code}
                        onChange={() => setPickedPlan(p.code)}
                        style={{ marginRight: 8 }} />
                <strong>{p.name}</strong>
                <div style={{ fontSize: 22, fontWeight: 600, margin: '8px 0' }}>
                  {p.monthlyBaseDollars != null
                    ? `${dollars(p.monthlyBaseCents)} / user / month`
                    : 'Custom volume pricing'}
                </div>
                <ul style={{ fontSize: 13, color: '#5f6368', paddingLeft: 18 }}>
                  {p.includedCasesPerUser != null && (
                    <li>{p.includedCasesPerUser} cases / user / month included</li>
                  )}
                  {p.overagePerCaseDollars != null && (
                    <li>${p.overagePerCaseDollars.toFixed(2)} per additional case</li>
                  )}
                  {p.isCustom && <li>1,000+ cases / month, custom contract</li>}
                  <li>All modules included</li>
                </ul>
              </label>
            ))}
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
            <label>
              Seats:
              <input type="number" min={1} value={pickedSeats}
                      onChange={(e) => setPickedSeats(e.target.value)}
                      style={{ width: 80, marginLeft: 8 }} />
            </label>
            <button className="btn btn--primary" onClick={handleStart} disabled={busy}>
              Start subscription
            </button>
          </div>
        </div>
      )}

      {/* Invoices */}
      <div className="admin-card">
        <h3>Invoices</h3>
        {invoices.length === 0 ? (
          <p>No invoices yet. The first invoice will appear after the current period closes.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Number</th><th>Issued</th><th>Amount</th>
                <th>Status</th><th>PayPal</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.InvoiceId}>
                  <td>{inv.Number}</td>
                  <td>{(inv.IssuedAt || '').slice(0, 10)}</td>
                  <td>{dollars(inv.AmountCents, inv.Currency)}</td>
                  <td><StatusPill status={inv.Status} /></td>
                  <td>{inv.PaypalInvoiceId || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
